const isLayer = (key, value) =>
  /^[A-Z]/.test(key) && value && typeof value === 'object' && !Array.isArray(value)

// Kept as a bare module-scope export ONLY because quickEditor.test.js imports
// it directly for offline Node coverage (`import { getQuickEditorLayerRows }`)
// and calls it standalone with plain data — not through a live component
// tree. A duplicate lives in `QuickEditor.scope` below for every in-tree
// caller (`Content.children`), since a bare module-scope reference read from
// inside a serialized factory reads as undefined once frank.toJSON's this
// component for platform delivery (workspace d6ea1142,
// project_frank_drops_module_local_helpers).
export const getQuickEditorLayerRows = (value, type, ownerKey) => {
  const rows =
    value && type && ownerKey
      ? [
          {
            key: '$root',
            label: 'editor.quick.root',
            depth: 0,
            hasChildren: false,
            isRoot: true,
            ownerType: type,
            ownerKey,
            path: [],
            textValue: value.text,
            colorValue: value.color,
            backgroundValue: value.background,
            paddingValue: value.padding,
            fontSizeValue: value.fontSize,
            flowValue: value.flow
          }
        ]
      : []
  const collect = (obj, depth, path = []) => {
    for (const key of Object.keys(obj || {}).filter((key) => isLayer(key, obj[key]))) {
      const layer = obj[key]
      const layerPath = [...path, key]
      const hasChildren = Object.keys(layer).some((childKey) => isLayer(childKey, layer[childKey]))
      rows.push({
        key: layerPath.join('.'),
        label: key,
        depth,
        hasChildren,
        ownerType: type,
        ownerKey,
        path: layerPath,
        textValue: layer.text,
        colorValue: layer.color,
        backgroundValue: layer.background,
        paddingValue: layer.padding,
        fontSizeValue: layer.fontSize,
        flowValue: layer.flow
      })
      if (hasChildren) collect(layer, depth + 1, layerPath)
    }
  }
  collect(value, 0)
  return rows
}

export const QuickEditor = {
  // FA204 / frank trap — every drag/inspector helper below lives in `scope`,
  // not module scope: a bare module-scope const/function silently reads as
  // undefined once this component is frank.toJSON'd for platform delivery
  // (workspace d6ea1142, project_frank_drops_module_local_helpers). Called
  // via `el.scope.<name>(...)` from any descendant factory/handler (`scope:`
  // merges onto every instance in this tree). Regular `function`/method
  // syntax throughout (not arrows) — `el.scope.foo()` binds `this` to
  // `el.scope`, so sibling helpers call each other via `this.<name>(...)`
  // instead of a bare identifier (object-literal properties can't see each
  // other as free variables). `isLayer`/`getQuickEditorLayerRows` are
  // duplicated here (module-scope originals stay for quickEditor.test.js's
  // offline import) — `Content.children` below reads whichever is
  // available, matching AppOrgSwitcher's `hide` dual-mode precedent.
  scope: {
    isLayer(key, value) {
      return /^[A-Z]/.test(key) && value && typeof value === 'object' && !Array.isArray(value)
    },

    getQuickEditorLayerRows(value, type, ownerKey) {
      const rows =
        value && type && ownerKey
          ? [
              {
                key: '$root',
                label: 'editor.quick.root',
                depth: 0,
                hasChildren: false,
                isRoot: true,
                ownerType: type,
                ownerKey,
                path: [],
                textValue: value.text,
                colorValue: value.color,
                backgroundValue: value.background,
                paddingValue: value.padding,
                fontSizeValue: value.fontSize,
                flowValue: value.flow
              }
            ]
          : []
      const collect = (obj, depth, path = []) => {
        for (const key of Object.keys(obj || {}).filter((key) => this.isLayer(key, obj[key]))) {
          const layer = obj[key]
          const layerPath = [...path, key]
          const hasChildren = Object.keys(layer).some((childKey) =>
            this.isLayer(childKey, layer[childKey])
          )
          rows.push({
            key: layerPath.join('.'),
            label: key,
            depth,
            hasChildren,
            ownerType: type,
            ownerKey,
            path: layerPath,
            textValue: layer.text,
            colorValue: layer.color,
            backgroundValue: layer.background,
            paddingValue: layer.padding,
            fontSizeValue: layer.fontSize,
            flowValue: layer.flow
          })
          if (hasChildren) collect(layer, depth + 1, layerPath)
        }
      }
      collect(value, 0)
      return rows
    },

    samePath(a = [], b = []) {
      return a.length === b.length && a.every((segment, index) => segment === b[index])
    },

    sameParentPath(a = [], b = []) {
      return this.samePath(a.slice(0, -1), b.slice(0, -1))
    },

    // Reviewed (border-habit sweep, design-direction.md §8): these three raw
    // style objects are OUT of the declarative border-habit class, kept as-is.
    // They're applied via `Object.assign(node.style, …)` to plain native DOM
    // nodes obtained from a live pointer-drag gesture (rowFromNode / cloneNode
    // below) — nodes DOMQL's token-resolving css-in-props layer never touches —
    // so `var(--color-line)` is the correct escape hatch here, not a bug: a
    // bare `'line'` string written straight to `.style` would be an invalid CSS
    // value. Functionally these are also a state affordance (dashed = "this is
    // being moved", solid = "this is the drop target"), the same family as the
    // sanctioned dashed state-ring, not decorative chrome.
    DRAG_SOURCE_STYLE: {
      background: 'var(--color-overlay)',
      opacity: '1',
      outlineColor: 'var(--color-line)',
      outlineStyle: 'dashed',
      outlineWidth: 'var(--spacing-X)',
      boxShadow: 'none',
      transform: 'none',
      zIndex: '1'
    },

    DROP_TARGET_STYLE: {
      background: 'var(--color-overlayStrong)',
      outlineColor: 'var(--color-line)',
      outlineStyle: 'solid',
      outlineWidth: 'var(--spacing-X)'
    },

    RESET_DRAG_STYLE: {
      background: '',
      opacity: '',
      outlineColor: '',
      outlineStyle: '',
      outlineWidth: '',
      boxShadow: '',
      transform: '',
      zIndex: ''
    },

    pathKey(path = []) {
      return path.join('')
    },

    layerRowKey(path = []) {
      return path.length ? encodeURIComponent(this.pathKey(path)) : '$root'
    },

    pathFromLayerRowKey(key) {
      return key && key !== '$root' ? decodeURIComponent(key).split('') : []
    },

    restoreSourceRowContent(quickEditor) {
      for (const item of quickEditor?.data?.dragSourceHiddenNodes || []) {
        if (item.node) item.node.style.visibility = item.visibility
      }
      if (quickEditor?.data) quickEditor.data.dragSourceHiddenNodes = null
    },

    hideSourceRowContent(quickEditor, row) {
      this.restoreSourceRowContent(quickEditor)
      if (!row?.node) return
      quickEditor.data.dragSourceHiddenNodes = [...row.node.children].map((node) => {
        const visibility = node.style.visibility
        node.style.visibility = 'hidden'
        return { node, visibility }
      })
    },

    restoreDragNodePosition(quickEditor) {
      const node = quickEditor?.data?.dragNode?.node
      const parent = quickEditor?.data?.dragOriginalParent
      const nextSibling = quickEditor?.data?.dragOriginalNextSibling
      if (node && parent && node.parentNode !== parent) {
        parent.insertBefore(node, nextSibling?.parentNode === parent ? nextSibling : null)
      } else if (
        node &&
        parent &&
        nextSibling?.parentNode === parent &&
        node.nextSibling !== nextSibling
      ) {
        parent.insertBefore(node, nextSibling)
      }
    },

    removeDragGhost(quickEditor) {
      quickEditor?.data?.dragGhost?.remove?.()
      if (quickEditor?.data) {
        quickEditor.data.dragGhost = null
        quickEditor.data.dragGhostOffset = null
      }
    },

    moveDragGhost(quickEditor, event) {
      const ghost = quickEditor?.data?.dragGhost
      const offset = quickEditor?.data?.dragGhostOffset
      if (!ghost || !offset) return
      ghost.style.transform = `translate3d(${event.clientX - offset.x}px, ${
        event.clientY - offset.y
      }px, 0)`
    },

    createDragGhost(quickEditor, row, event) {
      if (typeof document === 'undefined' || !row?.node) return
      this.removeDragGhost(quickEditor)

      const rect = row.node.getBoundingClientRect()
      const ghost = row.node.cloneNode(true)
      // Reviewed (border-habit sweep): same raw-DOM-write exception as
      // DRAG_SOURCE_STYLE/DROP_TARGET_STYLE above — this floating ghost clone is
      // also transient chrome over unpredictable live canvas content (the
      // canvas-glass exception's same reasoning), not a static declarative
      // surface, and `cloneNode`'d elements are outside css-in-props' reach too.
      Object.assign(ghost.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        pointerEvents: 'none',
        boxSizing: 'border-box',
        zIndex: '2147483647',
        background: 'var(--color-overlayStrong)',
        borderColor: 'var(--color-line)',
        borderStyle: 'solid',
        borderWidth: 'var(--spacing-X)',
        borderRadius: 'var(--spacing-Z)',
        opacity: '0.92',
        willChange: 'transform'
      })

      quickEditor.data.dragGhost = ghost
      quickEditor.data.dragGhostOffset = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      }
      document.body.appendChild(ghost)
      this.moveDragGhost(quickEditor, event)
    },

    resetDragNodes(quickEditor) {
      this.restoreSourceRowContent(quickEditor)
      quickEditor?.data?.dragNode?.setNodeStyles?.(this.RESET_DRAG_STYLE)
      quickEditor?.data?.dropNode?.setNodeStyles?.(this.RESET_DRAG_STYLE)
      this.removeDragGhost(quickEditor)
    },

    rowElement(el) {
      return el.parent || el
    },

    rowFromNode(quickEditor, node) {
      const rowKey = node?.getAttribute?.('data-qe-layer-path')
      if (!rowKey) return null
      const existing = quickEditor?.data?.layerRows?.[rowKey]
      if (existing?.el?.node === node) return existing
      return {
        el: {
          node,
          setNodeStyles: (styles) => Object.assign(node.style, styles)
        },
        type: node.getAttribute('data-qe-owner-type'),
        key: node.getAttribute('data-qe-owner-key'),
        path: this.pathFromLayerRowKey(rowKey),
        isRoot: node.getAttribute('data-qe-root') === '1'
      }
    },

    getRowUnderPointer(quickEditor, event) {
      const dragLayer = quickEditor?.data?.dragLayer
      if (!dragLayer) return null

      const nodes = Array.from(
        quickEditor?.Content?.node?.querySelectorAll?.('[data-qe-layer-path]') || []
      )
      const rows = nodes
        .map((node) => this.rowFromNode(quickEditor, node))
        .filter(
          (row) =>
            row?.el?.node &&
            !row.isRoot &&
            row.type === dragLayer.type &&
            row.key === dragLayer.key &&
            !this.samePath(row.path, dragLayer.path)
        )
        .map((row) => ({
          row,
          rect: row.el.node.getBoundingClientRect()
        }))
        .filter(({ rect }) => rect.width || rect.height)

      if (!rows.length) return null

      const pointerY = event.clientY
      return (
        rows.find(({ rect }) => pointerY >= rect.top && pointerY <= rect.bottom)?.row ||
        rows.reduce(
          (closest, current) => {
            const currentDistance = Math.abs(
              pointerY - (current.rect.top + current.rect.height / 2)
            )
            return currentDistance < closest.distance
              ? { row: current.row, distance: currentDistance }
              : closest
          },
          { row: null, distance: Infinity }
        ).row
      )
    },

    moveDragPlaceholder(quickEditor, row, position) {
      const dragNode = quickEditor?.data?.dragNode?.node
      const targetNode = row?.el?.node
      if (
        !dragNode ||
        !targetNode ||
        dragNode === targetNode ||
        dragNode.parentNode !== targetNode.parentNode
      ) {
        return
      }
      if (position === 'after') targetNode.after(dragNode)
      else targetNode.before(dragNode)
    },

    getDropPosition(row, event) {
      const rect = row?.el?.node?.getBoundingClientRect?.()
      if (!rect) return 'before'
      return event.clientY > rect.top + rect.height / 2 ? 'after' : 'before'
    },

    setDropLayerFromRow(quickEditor, row, event) {
      const dragLayer = quickEditor?.data?.dragLayer
      if (!dragLayer || !row || row.isRoot) return false
      if (
        dragLayer.key !== row.key ||
        dragLayer.type !== row.type ||
        this.pathKey(dragLayer.path) === this.pathKey(row.path)
      ) {
        quickEditor.data.dropLayer = null
        quickEditor.data.dropNode?.setNodeStyles?.(this.RESET_DRAG_STYLE)
        quickEditor.data.dropNode = null
        return false
      }
      if (quickEditor.data.dropNode !== row.el) {
        quickEditor.data.dropNode?.setNodeStyles?.(this.RESET_DRAG_STYLE)
        quickEditor.data.dropNode = row.el
      }
      quickEditor.data.dropLayer = {
        type: row.type,
        key: row.key,
        path: [...row.path],
        position: event ? this.getDropPosition(row, event) : 'before'
      }
      this.moveDragPlaceholder(quickEditor, row, quickEditor.data.dropLayer.position)
      row.el?.setNodeStyles?.(this.DROP_TARGET_STYLE)
      return true
    },

    updateDropLayerFromPointer(event, quickEditor) {
      return this.setDropLayerFromRow(
        quickEditor,
        this.getRowUnderPointer(quickEditor, event),
        event
      )
    },

    getDraggedSiblingOrder(quickEditor) {
      const dragLayer = quickEditor?.data?.dragLayer
      const parentNode = quickEditor?.data?.dragNode?.node?.parentNode
      if (!dragLayer || !parentNode) return null

      const rows = Array.from(parentNode.children)
        .map((node) => node.getAttribute?.('data-qe-layer-path'))
        .map((key, index) =>
          key ? this.rowFromNode(quickEditor, parentNode.children[index]) : null
        )
        .filter(
          (row) =>
            row?.path?.length &&
            row.type === dragLayer.type &&
            row.key === dragLayer.key &&
            this.sameParentPath(row.path, dragLayer.path)
        )

      return rows.length ? rows.map((row) => [...row.path]) : null
    },

    setDropLayer(event, el, s) {
      const quickEditor = el.call('getQuickEditor')
      if (!quickEditor?.data?.dragLayer || s.isRoot) return false
      event.preventDefault?.()
      event.stopPropagation?.()
      this.setDropLayerFromRow(
        quickEditor,
        {
          el,
          type: s.ownerType,
          key: s.ownerKey,
          path: [...s.path],
          isRoot: !!s.isRoot
        },
        event
      )
      return false
    },

    closeLayerInspector(el) {
      const quickEditor = el.call('getQuickEditor')
      if (!quickEditor?.data) return

      quickEditor.data.activeLayer = null
      quickEditor.setCanvasScope({ activeLayer: null })
      quickEditor.LayerInspector?.setNodeStyles({ display: 'none' })

      const fields = quickEditor.LayerInspector?.Fields
      for (const field of ['Text', 'Color', 'Background', 'Padding', 'FontSize', 'Flow']) {
        const input = fields?.[field]?.Input?.node
        if (input) input.value = ''
      }
    },

    async finishLayerPointerDrag(event, el) {
      const quickEditor = el.call('getQuickEditor')
      const dragLayer = quickEditor?.data?.dragLayer
      if (!quickEditor?.data) return

      this.updateDropLayerFromPointer(event, quickEditor)
      const dropLayer = quickEditor.data.dropLayer

      quickEditor.data.dragCleanup?.()
      quickEditor.data.dragCleanup = null
      this.resetDragNodes(quickEditor)

      let saved = false
      if (
        dragLayer?.path?.length &&
        dropLayer?.path?.length &&
        dragLayer.type === dropLayer.type &&
        dragLayer.key === dropLayer.key &&
        !this.samePath(dragLayer.path, dropLayer.path)
      ) {
        saved = await el.call(
          'reorderQuickEditorLayer',
          dropLayer.path,
          dropLayer.position,
          this.sameParentPath(dragLayer.path, dropLayer.path)
            ? this.getDraggedSiblingOrder(quickEditor)
            : null
        )
      }
      if (!saved) {
        this.restoreDragNodePosition(quickEditor)
      }

      quickEditor.data.dragLayer = null
      quickEditor.data.dropLayer = null
      quickEditor.data.dragNode = null
      quickEditor.data.dropNode = null
      quickEditor.data.dragOriginalParent = null
      quickEditor.data.dragOriginalNextSibling = null
    },

    startLayerPointerDrag(event, el, s) {
      if (s.isRoot || event.button !== 0) return
      const quickEditor = el.call('getQuickEditor')
      if (!quickEditor) return

      event.stopPropagation?.()
      event.preventDefault?.()

      const row = this.rowElement(el)
      this.resetDragNodes(quickEditor)
      quickEditor.data.dragLayer = {
        type: s.ownerType,
        key: s.ownerKey,
        path: [...s.path]
      }
      quickEditor.data.dropLayer = null
      quickEditor.data.dragNode = row
      quickEditor.data.dropNode = null
      quickEditor.data.dragOriginalParent = row.node?.parentNode || null
      quickEditor.data.dragOriginalNextSibling = row.node?.nextSibling || null
      this.createDragGhost(quickEditor, row, event)
      row.setNodeStyles?.(this.DRAG_SOURCE_STYLE)
      this.hideSourceRowContent(quickEditor, row)

      if (typeof document !== 'undefined') {
        const onPointerMove = (moveEvent) => {
          this.moveDragGhost(quickEditor, moveEvent)
          this.updateDropLayerFromPointer(moveEvent, quickEditor)
        }
        const onPointerUp = (upEvent) => {
          document.removeEventListener('pointermove', onPointerMove, true)
          this.updateDropLayerFromPointer(upEvent, quickEditor)
          this.finishLayerPointerDrag(upEvent, row)
        }
        quickEditor.data.dragCleanup = () => {
          document.removeEventListener('pointermove', onPointerMove, true)
        }
        document.addEventListener('pointermove', onPointerMove, true)
        document.addEventListener('pointerup', onPointerUp, { once: true, capture: true })
      }
    }
  },

  state: {
    recents: [],
    recentsIndex: 0,
    showLayers: true
  },
  data: {
    prevSelected: null,
    activeLayer: null,
    needsRefresh: false,
    pendingDelete: null,
    dragLayer: null,
    dropLayer: null,
    dragCleanup: null,
    dragGhost: null,
    dragGhostOffset: null,
    dragSourceHiddenNodes: null,
    dragOriginalParent: null,
    dragOriginalNextSibling: null,
    layerRows: null
  },

  Header: {
    flow: 'x',
    align: 'center start',
    padding: 'Y Y1',
    gap: 'Z',
    width: '100%',

    Text: {
      tag: 'span',
      fontSize: 'Z2',
      maxWidth: '65%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },

    QuickEditorNavbar: {
      margin: '- - - auto'
    }
  },

  Content: {
    tag: 'div',
    class: 'layers',
    fontSize: 'A1',
    overflow: 'hidden auto',
    maxHeight: 'max(120px, min(42dvh, calc(100dvh - 300px)))',
    padding: '0 Y1 Y1',
    childrenAs: 'state',
    children: (el) => {
      const selected = el.getSelectedKey()
      const project = el.getRootState('data')
      const type = Object.hasOwn(project?.components || {}, selected)
        ? 'components'
        : Object.hasOwn(project?.pages || {}, selected)
          ? 'pages'
          : null
      const value = type ? project[type][selected] : {}
      // Dual-mode read (AppOrgSwitcher `hide` precedent): the live/frank-
      // served tree always has `el.scope`, so it takes the scoped copy; the
      // module-scope `getQuickEditorLayerRows` fallback only fires when this
      // factory is called directly with a fake `el` (quickEditor.test.js
      // `QuickEditor.Content.children({...})`, no `.scope`).
      return el.scope?.getQuickEditorLayerRows
        ? el.scope.getQuickEditorLayerRows(value, type, selected)
        : getQuickEditorLayerRows(value, type, selected)
    },
    childProps: {
      position: 'relative',
      flow: 'x',
      align: 'center',
      width: '100%',
      paddingLeft: (el, s) => `${6 + s.depth * 12}px`,
      round: 'radiusControl',
      role: 'treeitem',
      draggable: false,
      userSelect: 'none',
      attr: {
        'data-qe-layer-path': (el, s) => el.scope.layerRowKey(s.path),
        'data-qe-owner-key': (el, s) => s.ownerKey || '',
        'data-qe-owner-type': (el, s) => s.ownerType || '',
        'data-qe-root': (el, s) => (s.isRoot ? '1' : '0')
      },
      onRender: (el, s) => {
        const quickEditor = el.call('getQuickEditor')
        if (!quickEditor?.data) return
        if (!quickEditor.data.layerRows) quickEditor.data.layerRows = {}
        quickEditor.data.layerRows[el.scope.layerRowKey(s.path)] = {
          el,
          type: s.ownerType,
          key: s.ownerKey,
          path: [...s.path],
          isRoot: !!s.isRoot
        }
      },
      opacity: (el, s) => {
        const dragLayer = el.call('getQuickEditor')?.data?.dragLayer
        return dragLayer?.key === s.ownerKey && dragLayer?.path?.join('.') === s.path?.join('.')
          ? '0.45'
          : '1'
      },
      // Real bug found while sweeping (adjacent to the border-habit class,
      // design-direction.md §8/§4.3): this row was using the `line` stroke
      // token as a STATE FILL for both the drop-target indicator and hover —
      // `line` (`gray13`/`gray5`) is a strong, opaque rule colour, not a
      // wash, so on light theme this rendered as a heavy flat-gray block
      // instead of a subtle tint. Corrected to the canonical state tokens
      // (§8.1): `selected` for "this row is the active drop target",
      // `hover` for pointer hover.
      background: (el, s) => {
        const dropLayer = el.call('getQuickEditor')?.data?.dropLayer
        return dropLayer?.key === s.ownerKey && dropLayer?.path?.join('.') === s.path?.join('.')
          ? 'selected'
          : 'transparent'
      },
      ':hover': { background: 'hover' },
      onPointerEnter: (event, el, s) => el.scope.setDropLayer(event, el, s),
      DragHandle: {
        tag: 'button',
        type: 'button',
        display: (el, s) => (s.isRoot ? 'none' : 'inline-flex'),
        align: 'center center',
        flex: '0 0 auto',
        boxSize: 'A A',
        margin: '- X - -',
        padding: '0',
        border: '0',
        background: 'transparent',
        color: 'caption',
        opacity: '0.72',
        cursor: 'grab',
        title: (el) => el.call('polyglot', 'editor.quick.dragToReorder'),
        aria: {
          label: (el, s) =>
            el
              .call('polyglot', 'editor.quick.dragLayer')
              .replace('{layer}', el.call('polyglot', s.label || ''))
        },
        text: '⠿',
        lineHeight: '1',
        fontSize: 'Z1',
        onPointerDown: (event, el, s) => el.scope.startLayerPointerDrag(event, el, s),
        ':hover': {
          color: 'title',
          opacity: '1'
        },
        ':active': {
          cursor: 'grabbing'
        }
      },
      Layer: {
        tag: 'button',
        type: 'button',
        flex: '1',
        padding: 'Y Z',
        border: '0',
        background: 'transparent',
        color: 'paragraph',
        textAlign: 'left',
        cursor: 'pointer',
        text: (el, s) =>
          `${s.isRoot ? '◉' : s.hasChildren ? '▸' : '·'} ${el.call('polyglot', s.label || '')}`,
        draggable: false,
        onClick: (event, el, s) => {
          const quickEditor = el.call('getQuickEditor')
          if (!quickEditor) return
          quickEditor.data.activeLayer = {
            type: s.ownerType,
            key: s.ownerKey,
            path: s.path
          }
          quickEditor.setCanvasScope({ activeLayer: s.path })
          const inspector = quickEditor.LayerInspector
          inspector?.setNodeStyles({ display: 'flex' })
          if (inspector?.Header?.Title?.node) inspector.Header.Title.node.textContent = s.label
          const fields = inspector?.Fields
          const values = {
            Text: s.textValue,
            Color: s.colorValue,
            Background: s.backgroundValue,
            Padding: s.paddingValue,
            FontSize: s.fontSizeValue,
            Flow: s.flowValue
          }
          for (const [field, value] of Object.entries(values)) {
            const input = fields?.[field]?.Input?.node
            if (input) input.value = value || ''
          }
        }
      },
      Delete: {
        extends: 'IconButton',
        icon: 'crossmark',
        title: (el, s) =>
          el
            .call('polyglot', 'editor.quick.deleteLayer')
            .replace('{layer}', el.call('polyglot', s.label || '')),
        aria: {
          label: (el, s) =>
            el
              .call('polyglot', 'editor.quick.deleteLayer')
              .replace('{layer}', el.call('polyglot', s.label || ''))
        },
        margin: '- Y - -',
        opacity: '0.6',
        display: (el, s) => (s.isRoot ? 'none' : 'inline-flex'),
        onClick: (event, el, s) => {
          event.stopPropagation()
          const quickEditor = el.call('getQuickEditor')
          const popup = quickEditor?.DeleteConfirm
          if (!quickEditor || !popup) return
          quickEditor.data.pendingDelete = {
            type: s.ownerType,
            key: s.ownerKey,
            path: [...s.path],
            label: s.label
          }
          if (popup.Text?.node) {
            popup.Text.node.textContent = el
              .call('polyglot', 'editor.quickEditor.deleteConfirm')
              .replace('{name}', s.label || '')
          }
          const editorRect = quickEditor.node?.getBoundingClientRect?.()
          const buttonRect = el.node?.getBoundingClientRect?.()
          const top =
            editorRect && buttonRect
              ? Math.min(buttonRect.bottom - editorRect.top, editorRect.height - 120)
              : 0
          popup.setNodeStyles({ display: 'flex', top: `${Math.max(0, top)}px` })
        }
      }
    }
  },

  DeleteConfirm: {
    display: 'none',
    position: 'absolute',
    right: 'Y',
    zIndex: '2',
    flow: 'y',
    gap: 'Y',
    width: 'G',
    padding: 'Z',
    round: 'radiusControl',
    // Fill-contrast popup (was a `line` border — border-habit sweep,
    // design-direction.md §8) — `theme: 'common-box'`'s own opaque fill +
    // the `overlay` elevation shadow already separate it from the canvas.
    theme: 'common-box',
    shadow: 'overlay',
    Text: {
      tag: 'span',
      text: '{{ editor.quick.deleteConfirm | polyglot }}'
    },
    Actions: {
      flow: 'x',
      gap: 'Y',
      align: 'center end',
      Cancel: {
        extends: 'Button',
        text: '{{ common.action.cancel | polyglot }}',
        theme: 'secondary',
        padding: 'Y Z',
        onClick: (event, el) => {
          event.stopPropagation()
          const quickEditor = el.call('getQuickEditor')
          if (quickEditor?.data) quickEditor.data.pendingDelete = null
          quickEditor?.DeleteConfirm?.setNodeStyles({ display: 'none' })
        }
      },
      Confirm: {
        extends: 'Button',
        text: '{{ common.action.delete | polyglot }}',
        theme: 'secondary',
        color: 'red',
        padding: 'Y Z',
        onClick: async (event, el) => {
          event.stopPropagation()
          const quickEditor = el.call('getQuickEditor')
          const target = quickEditor?.data?.pendingDelete
          if (!target) return
          quickEditor.data.pendingDelete = null
          quickEditor.DeleteConfirm?.setNodeStyles({ display: 'none' })
          await el.call('deleteQuickEditorLayer', target.type, target.key, target.path)
        }
      }
    }
  },

  AddControls: {
    flow: 'y',
    gap: 'Y',
    padding: 'Z Y1 Y1',
    // Top divider removed (border-habit sweep, design-direction.md §8) —
    // this panel's own `theme: 'common-box'` fill (declared at the QuickEditor
    // root) already separates every section; padding carries the rest.
    Label: {
      tag: 'span',
      text: '{{ editor.quick.addElement | polyglot }}',
      color: 'caption',
      fontSize: 'Y2'
    },
    Buttons: {
      flow: 'x',
      flexWrap: 'wrap',
      gap: 'Y',
      childProps: {
        extends: 'Button',
        theme: 'secondary',
        padding: 'Y Z'
      },
      Container: {
        text: '{{ editor.quick.el.container | polyglot }}',
        onClick: (ev, el) => el.call('addQuickEditorLayer', 'container')
      },
      Heading: {
        text: '{{ editor.quick.el.heading | polyglot }}',
        onClick: (ev, el) => el.call('addQuickEditorLayer', 'heading')
      },
      Text: {
        text: '{{ editor.quick.el.text | polyglot }}',
        onClick: (ev, el) => el.call('addQuickEditorLayer', 'text')
      },
      Button: {
        text: '{{ editor.quick.el.button | polyglot }}',
        onClick: (ev, el) => el.call('addQuickEditorLayer', 'button')
      }
    }
  },

  LayerInspector: {
    display: 'none',
    flow: 'y',
    gap: 'Z',
    padding: 'Z Y1 Y1',
    overflow: 'hidden auto',
    maxHeight: 'max(160px, calc(100dvh - 460px))',
    // Top divider removed (border-habit sweep, design-direction.md §8) —
    // same reasoning as AddControls above.
    Header: {
      flow: 'x',
      align: 'center',
      gap: 'Y',
      Title: {
        tag: 'span',
        text: '{{ editor.quick.properties | polyglot }}',
        fontWeight: '700',
        flex: '1'
      },
      Collapse: {
        extends: 'IconButton',
        icon: 'arrow angle down',
        title: (el) => el.call('polyglot', 'editor.quick.hideProperties'),
        aria: { label: (el) => el.call('polyglot', 'editor.quick.hideProperties') },
        margin: '- - - auto',
        opacity: '0.72',
        onClick: (event, el) => {
          event.preventDefault()
          event.stopPropagation()
          el.scope.closeLayerInspector(el)
        },
        // Same `line`-as-state-fill bug as Content.childProps above —
        // corrected to the canonical `hover` token.
        ':hover': {
          opacity: '1',
          background: 'hover'
        }
      }
    },
    Fields: {
      flow: 'y',
      gap: 'Y',
      childProps: {
        flow: 'y',
        gap: 'X',
        Label: {
          tag: 'label',
          color: 'caption',
          fontSize: 'Y2'
        },
        Input: {
          tag: 'input',
          width: '100%',
          padding: 'Y Z',
          round: 'radiusControl',
          // Real bug found while sweeping (border-habit, design-direction.md
          // §8): `background: 'field'` is not a colour token (`field` is
          // only a THEME name, in company/packages/brand/designSystem/
          // theme.js — the sole such use in this package) so it never
          // resolved, and the hand-rolled `line` border was standing in for
          // the branded field's own border. `theme: 'field'` is the real
          // component (§2.5 exception #4 — field keeps its own border,
          // matching IntegrationConfigureForm's SecretField.Input/
          // DisplayName.Input in this same sweep).
          theme: 'field',
          color: 'title'
        }
      },
      Text: {
        Label: { text: '{{ editor.quick.field.text | polyglot }}' },
        Input: {
          placeholder: (el) => el.call('polyglot', 'editor.quick.field.textPlaceholder'),
          onChange: (ev, el) => el.call('updateQuickEditorLayer', 'text', ev.target.value)
        }
      },
      Color: {
        Label: { text: '{{ editor.quick.field.color | polyglot }}' },
        Input: {
          placeholder: 'title, blue, #fff…',
          onChange: (ev, el) => el.call('updateQuickEditorLayer', 'color', ev.target.value)
        }
      },
      Background: {
        Label: { text: '{{ editor.quick.field.background | polyglot }}' },
        Input: {
          placeholder: 'document, gray3…',
          onChange: (ev, el) => el.call('updateQuickEditorLayer', 'background', ev.target.value)
        }
      },
      Padding: {
        Label: { text: '{{ editor.quick.field.padding | polyglot }}' },
        Input: {
          placeholder: 'Z, A, Y Z…',
          onChange: (ev, el) => el.call('updateQuickEditorLayer', 'padding', ev.target.value)
        }
      },
      FontSize: {
        Label: { text: '{{ editor.quick.field.fontSize | polyglot }}' },
        Input: {
          placeholder: 'Z1, A, B…',
          onChange: (ev, el) => el.call('updateQuickEditorLayer', 'fontSize', ev.target.value)
        }
      },
      Flow: {
        Label: { text: '{{ editor.quick.field.flow | polyglot }}' },
        Input: {
          placeholder: 'x, y, x wrap…',
          onChange: (ev, el) => el.call('updateQuickEditorLayer', 'flow', ev.target.value)
        }
      }
    }
  },

  PromptAI: {
    margin: 'auto - -',
    order: 2,
    hide: true
  },

  flow: 'y',
  gap: '0',
  position: 'absolute',
  top: 'C+X2',
  padding: '0',
  left: '--canvas-gutter',
  round: 'radiusControl',
  // Fill-contrast panel (was a `line` border — border-habit sweep,
  // design-direction.md §8) — `theme: 'common-box'` already supplies its own
  // opaque fill, same precedent as DynamicIsland (this floats over the
  // canvas the same way).
  theme: 'common-box',
  minWidth: 'H',
  width: 'H',
  maxHeight: 'calc(100dvh - 96px)',
  overflow: 'hidden',
  overscrollBehavior: 'contain',
  fontSize: 'Y2',
  opacity: '0',

  onRender: (el) => {
    el.node.style.opacity = '0'
    el.node.style.pointerEvents = 'none'

    const resetInspector = () => {
      el.data.activeLayer = null
      el.setCanvasScope({ activeLayer: null })

      const inspector = el.LayerInspector
      inspector?.setNodeStyles({ display: 'none' })
      const fields = inspector?.Fields
      for (const field of ['Text', 'Color', 'Background', 'Padding', 'FontSize', 'Flow']) {
        const input = fields?.[field]?.Input?.node
        if (input) input.value = ''
      }
    }

    setInterval(() => {
      if (!el.node) return

      const selected = el.getSelectedKey()
      const editorActive = el.getCanvasScope('editorActive')

      if (!selected || editorActive === false) {
        if (!selected && editorActive !== false) {
          el.setCanvasScope({ editorActive: false })
        }
        if (el.node.style.opacity !== '0') {
          el.node.style.opacity = '0'
          el.node.style.pointerEvents = 'none'
        }
        if (el.data.prevSelected) {
          el.data.prevSelected = null
        }
        if (el.data.activeLayer) resetInspector()
        return
      }

      if (el.node.style.opacity === '0') {
        el.node.style.opacity = '1'
        el.node.style.pointerEvents = ''
      }

      const selectedChanged = selected !== el.data.prevSelected
      if (selectedChanged) {
        const selectedEl = el.getSelected()
        if (selectedEl?.state?.parse) {
          resetInspector()
          const textEl = el.Header?.Text
          if (textEl?.node) textEl.node.textContent = selected
          el.data.prevSelected = selected
        }
      }
      el.data.needsRefresh = false
    }, 100)
  }
}
